import { useCallback, useMemo, useState } from "react";

/** The stable identity of a row, for selection across re-renders. */
export const rowKey = (row) => row?.public_id ?? `${row?.name ?? ""}/${row?.key ?? ""}`;

/**
 * Multi-select state for a table.
 *
 * @param {object[]} rows The rows currently visible (the header checkbox applies to these).
 * @param {(row: object) => string} [keyOf]
 */
export const useRowSelection = (rows = [], keyOf = rowKey) => {
  const [selected, setSelected] = useState(() => new Set());

  const visibleKeys = useMemo(() => (rows ?? []).map((row) => keyOf(row)), [rows, keyOf]);

  const allSelected = visibleKeys.length > 0 && visibleKeys.every((key) => selected.has(key));

  const toggle = useCallback((key) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected((current) => {
      const next = new Set(current);

      if (visibleKeys.length > 0 && visibleKeys.every((key) => next.has(key))) {
        visibleKeys.forEach((key) => next.delete(key));
      } else {
        visibleKeys.forEach((key) => next.add(key));
      }

      return next;
    });
  }, [visibleKeys]);

  const clear = useCallback(() => setSelected(new Set()), []);

  const isSelected = useCallback((key) => selected.has(key), [selected]);

  return {
    selected,
    count: selected.size,
    allSelected,
    toggle,
    toggleAll,
    clear,
    isSelected,
    keys: [...selected],
    rows: (rows ?? []).filter((row) => selected.has(keyOf(row))),
  };
};

export default useRowSelection;
